import { useCallback, useEffect, useMemo, useState } from "react";
import useApi from "./useApi";
import { getRestaurantCompleteCheck } from "../services/restaurantService";
import { useStripeDone } from "./useStripeStatus";

const STEPS = [
  {
    key: "banking",
    labelKey: "dashboard.restaurant.activationSteps.banking",
    route: "dashboard/settings/bank",
  },
  {
    key: "schedules",
    labelKey: "dashboard.restaurant.activationSteps.schedules",
    route: "dashboard/settings/profile",
  },
  {
    key: "dish",
    labelKey: "dashboard.restaurant.activationSteps.dish",
    route: "dashboard/products",
  },
];

export const useRestaurantActivationStatus = (restaurantId) => {
  const [activationStatus, setActivationStatus] = useState({
    checks: null,
    completedRequired: 0,
    totalRequired: 3,
    progressPercent: 0,
    missingSteps: STEPS.map((step) => step.key),
  });
  const { request: getCompleteCheckRequest, loading } = useApi(
    getRestaurantCompleteCheck,
  );
  const { isStripeDone, stripeUrl, clearStripeStatus, refetchStripeStatus } =
    useStripeDone(restaurantId);

  const refreshActivationStatus = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const response = await getCompleteCheckRequest(restaurantId);
      setActivationStatus((previous) => ({
        ...previous,
        ...response.data,
      }));
    } catch (error) {
      console.error(error);
    }
    await refetchStripeStatus();
  }, [getCompleteCheckRequest, restaurantId, refetchStripeStatus]);

  useEffect(() => {
    if (!restaurantId) {
      setActivationStatus({
        checks: null,
        completedRequired: 0,
        totalRequired: 3,
        progressPercent: 0,
        missingSteps: STEPS.map((step) => step.key),
      });
      return;
    }

    refreshActivationStatus();
  }, [restaurantId, refreshActivationStatus]);

  const steps = useMemo(
    () =>
      STEPS.map((step) => ({
        ...step,
        done: !!activationStatus.checks?.[step.key],
      })),
    [activationStatus.checks],
  );

  const missingSteps = useMemo(
    () => steps.filter((step) => !step.done),
    [steps],
  );

  const canActivateRestaurant = missingSteps.length === 0;

  return {
    ...activationStatus,
    checks: activationStatus.checks ?? {
      banking: false,
      schedules: false,
      dish: false,
    },
    steps,
    missingSteps,
    firstMissingStepRoute: missingSteps[0]?.route || null,
    canActivateRestaurant,
    isStripeDone: activationStatus.checks?.banking ?? isStripeDone,
    stripeUrl,
    loading,
    refreshActivationStatus,
    clearStripeStatus,
  };
};

export default useRestaurantActivationStatus;
